import React from 'react';

import { Facebook, Instagram, LinkedIn, Phone } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import logoFooterWordmark from './assets/logo-footer-wordmark.svg';
import logoSeePeopleFirst from './assets/logo-see-people-first.svg';
import { FONT_FAMILY, PRIMARY_TEAL, WHITE } from './colors';

import type { FooterLinkColumn } from './types';

const STATIC_SOCIAL_LINKS = [
  { Icon: Facebook, url: 'https://www.facebook.com/ScoutTalentRecruitment/' },
  { Icon: LinkedIn, url: 'https://www.linkedin.com/company/scouttalent/' },
  { Icon: Instagram, url: 'https://www.instagram.com/scouttalentgroup/' }
];

interface FooterProps {
  linkColumns: FooterLinkColumn[];
  currentYear: number;
  privacyPolicyUrl: string;
  termsUrl: string | null;
  linkUrls: Record<string, string>;
  phoneUrl: string;
}

const Footer = ({
  linkColumns,
  currentYear,
  privacyPolicyUrl,
  termsUrl,
  linkUrls,
  phoneUrl
}: FooterProps) => {
  const socialLinks = [...STATIC_SOCIAL_LINKS, { Icon: Phone, url: phoneUrl }];

  return (
    <Box sx={{ backgroundColor: '#03211F', padding: '64px 64px 35px' }}>
      <Box
        sx={{
          maxWidth: '1152px',
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: '48px'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
          <Box
            alt="See People First"
            component="img"
            src={logoSeePeopleFirst}
            sx={{ width: '198px' }}
          />
          <Box sx={{ display: 'flex', gap: '13px', alignItems: 'center' }}>
            {socialLinks.map(({ Icon, url }, index) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                component="a"
                href={url}
                rel="noreferrer"
                sx={{ display: 'flex' }}
                target="_blank"
              >
                <Icon sx={{ color: PRIMARY_TEAL, fontSize: '30px' }} />
              </Box>
            ))}
          </Box>
        </Box>

        {linkColumns.map((column) => {
          const headingUrl = linkUrls[column.heading];

          return (
            <Box
              key={column.heading}
              sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              <Typography
                component={headingUrl ? 'a' : 'p'}
                href={headingUrl}
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 800,
                  fontSize: '20px',
                  color: WHITE,
                  textDecoration: 'none'
                }}
              >
                {column.heading}
              </Typography>
              {column.links.map((link) => {
                const url = linkUrls[link.label];

                return (
                  <Typography
                    key={link.label}
                    component={url ? 'a' : 'p'}
                    href={url}
                    sx={{
                      fontFamily: FONT_FAMILY,
                      fontWeight: 500,
                      fontSize: '16px',
                      color: WHITE,
                      opacity: 0.8,
                      textDecoration: 'none'
                    }}
                  >
                    {link.label}
                  </Typography>
                );
              })}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          maxWidth: '1152px',
          margin: '80px auto 0',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '16px'
        }}
      >
        <Box
          alt="Scout Talent"
          component="img"
          src={logoFooterWordmark}
          sx={{ width: '100%', maxWidth: '1152px' }}
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            width: '100%',
            justifyContent: 'space-between'
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              fontSize: '11.65px',
              color: 'rgba(255,255,255,0.2)'
            }}
          >
            Scout Talent © {currentYear}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              fontSize: '11.65px',
              color: 'rgba(255,255,255,0.2)'
            }}
          >
            <Box
              component="a"
              href={privacyPolicyUrl}
              rel="noreferrer"
              sx={{ color: 'inherit', textDecoration: 'underline' }}
              target="_blank"
            >
              Privacy Policy
            </Box>
            {' | '}
            <Box
              component="a"
              href={termsUrl}
              rel="noreferrer"
              sx={{ color: 'inherit', textDecoration: 'underline' }}
              target="_blank"
            >
              Ts and Cs
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
